import { CREATE_USER_RECKONING } from '../../mutations/UserReckoning';
import { FINISH_MATCHING_SURVEY, FINISH_SURVEY } from '../../mutations/UserSurvey';
import { FeatureModel } from '../Feature/FeatureModel';
import { GET_SESSION, GET_SESSION_USER_SEGMENTS, SESSION_OF_TYPE } from '../../queries/Session';
import { IResponse } from '../Response/Response';
import { IResponseStore } from '../Response/ResponseStore';
import { IRootStore } from '../Root/RootStore';
import { ISession, SessionModel } from './SessionModel';
import { ITreeNav } from '../TreeNav/TreeNav';
import { Image } from 'react-native';
import { Instance, cast, flow, getParent, getRoot, types } from 'mobx-state-tree';
import { SessionStepModel } from '../Session/SessionStepModel';
import { UPDATE_USER_SESSION } from '../../mutations/UserSession';
import { captureException } from '../../../utils/sentry/sentry';
import { client, clientFunction } from '../../../config/apollo/client';
import { filterDefinedFields } from '../../../utils/filterFields';
import { logAmplitude, setProperty } from '../../../utils/analytics/analytics';

export interface SessionStepInput {
 userSurvey: string | null;
 answers: string[] | null,
 value: IResponse['value'] | null,
 freeText: IResponse['freeText'] | null,
 contentId: string | null,
 contentType: string | null;
 myExerciceId: string | null;
 sessionStep: string | null;
 review: number | null;
 order: number | null;
}

export const SessionStore = types
  .model('SessionStore', {
    session: types.array(types.maybe(SessionModel)),
    state: types.maybe(types.enumeration(['pending', 'done', 'error'])),
  })
  .actions(self => {
    const getContentId: (arg0: any) => string | null = (step: any) => {
      switch (step.contentType) {
      case 'SURVEY':
        return step.survey._id;
      case 'CONTENT_TEXT':
        return step.content._id;
      case 'CONTENT_AUDIO':
        return step.content._id;
      case 'CONTENT_VIDEO':
        return step.content._id;
      case 'FRAME':
        return null;
      case 'ACTIVITY':
        return step.activity._id;
      case 'EXERCISE':
        return step.exercise._id;
      case 'QUESTION':
        return step.question._id;
      case 'RADAR':
        return step.radarFeature._id;
      default:
        return null;
      }
    };


    const fetchSession = flow(function* (id: string, pathId?: string, reckoningId?: string) {
      self.state = 'pending';
      const languageRequest = (getParent(self) as any).language.name.key;
      console.log('Session.fetchSession', id, pathId || '');
      try {
        const { data }: any = yield clientFunction(languageRequest).query({ query: GET_SESSION, variables: { 'id': id }, fetchPolicy: 'no-cache' });
        let path = null;
        if(pathId) {
          path = (getParent(self) as any).path.getPath(pathId);
        }
        if (data) {
          loadSession([data.session],path, reckoningId);
        }
        console.log('Session.fetchSession loaded', id, !!data?.session);
        self.state = 'done';
      } catch (error) {
        console.log('Session.fetchSession failed', id, String(error));
        self.state = 'error';
        captureException(error);
        return null;
      }
    });

    // refresh: fetch even when the session is already in the (persisted) store,
    // so its steps reflect the current server content
    const fetchSessionWrapperIfNotExistFromPath = flow(function* (id: string, pathId: string, withContent = false, refresh = false) {
      if (self.session) {
        const index = self.session.findIndex(s => s && s._id === id && s.path === pathId);
        if (refresh || index === -1 || self.session[index]?.language !== (getParent(self) as any).language.name.key) {
          yield fetchSession(id, pathId);

          if(withContent) {
            yield loadContentOfSession(pathId, id);
          }

        } else {
          self.state = 'done';
        }
      }
    });

    const fetchSessionWrapperIfNotExistFromReckoning = flow(function* (id: string, reckoningId: string, withContent = false, refresh = false) {
      if (self.session) {
        const index = self.session.findIndex(s => s && s._id === id && s.reckoning === reckoningId);
        if (refresh || index === -1 || self.session[index]?.language !== (getParent(self) as any).language.name.key) {
          yield fetchSession(id, undefined, reckoningId);
          if(withContent) {
            yield loadContentOfSession(null, id);
          }
        } else {
          self.state = 'done';
        }
      }
    });

    const fetchSessionWrapperIfNotExistIndependent = flow(function* (id: string, withContent = false, refresh = false) {
      if (self.session) {
        const index = self.session.findIndex(s => s && s._id === id);
        const contentRemainToLoad = index!==-1 && !self.session[index].contentLoaded;
        const forceFetch = contentRemainToLoad && withContent;
        if (refresh || forceFetch || index === -1 || self.session[index]?.language !== (getParent(self) as any).language.name.key) {
          yield fetchSession(id);

          if(withContent) {
            yield loadContentOfSession(null, id);
          }
        } else {
          self.state = 'done';
        }
      }
    });

    const loadSession = (sessionsInput: [any], path: any, reckoningId: any = null) => {
      const sessions = sessionsInput?.map(session => {
        const pathExist = !!path;
        const reckoningExist = !!reckoningId;
        const done = pathExist && session?.userSession?.done;

        const sessionSteps = session.mySegmentSteps.map((step: any) => {
          if(step.contentType === 'QUESTION' && !step.question) {
            return null;
          }
          let skippable = false;
          if ('skippable' in step) {
            skippable = step.skippable;
          }
          const contentId = getContentId(step);
          // the question set of a standalone question/frame; SURVEY steps carry the survey as content
          const questionSet = step.contentType !== 'SURVEY' && step.survey ? step.survey._id : null;
          delete step.survey;
          const frame = step.frame ? {
            type: step.frame.layout,
            image: step.frame.image ?? undefined,
            emoji: step.frame.emoji ?? undefined,
            mainTitle: step.frame.title?.value ?? '',
            secondaryTitle: step.frame.title?.secondaryValue ?? '',
            text: (step.frame.texts || []).map((t: any) => ({ value: t?.value, secondaryValue: t?.secondaryValue })),
          } : null;
          delete step.frame;
          if (frame?.image) {
            // download the picture with the steps: a frame shown before its
            // picture has arrived otherwise opens empty
            Image.prefetch(frame.image).catch(() => undefined);
          }
          return ({
            ...filterDefinedFields(step,SessionStepModel.properties),
            contentId,
            questionSet,
            frame,
            sessionStepId: step._id,
            review: 0,
            skippable,
            ownId: step._id,
            exerciseDecided: !!step.myExerciceId,
            type: session?.sessionType
          });
        }).filter(x=>x).sort((a: any, b: any) => {
          return a.order > b.order ? 1 : -1;

        });
        let userSessionInfo = {};
        if(session?.userSession) {
          userSessionInfo = {
            userSessionId: session?.userSession?._id,
            wasDoneToday: session?.userSession?.wasDoneToday,
            userSessionOrder: session?.userSession?.order,
          };
        }

        return {
          ...filterDefinedFields(session,SessionModel.properties),
          ...userSessionInfo,
          sessionSteps,
          done,
          contentLoaded: false,
          language: path?.language ? path.language : (getParent(self) as any).language.name.key,
          fromPath: pathExist,
          fromReckoning: reckoningExist,
          path: path?._id ? path._id : null,
          reckoning: reckoningId ? reckoningId : null,
          type: session?.sessionType
        };
      });
      sessions.forEach(session => {
        const index = self.session?.findIndex(s => s && s._id === session._id);
        if (index === -1) {
          self.session?.push(session);
        } else {
          self.session[index] = session;
        }
      });

    };
    const loadAISessions = (sessions: any[]) => {

      sessions.map(session => {
        loadAISession(session);
      });
      (getParent(self) as any).user.setNbAiSessions(sessions.length);
    };
    const loadAISession = (session: any) => {


      const sessionSteps = session?.steps.map((step: any) => {

        return ({
          contentId: getContentId(step),
          contentType: step?.contentType,
          text: step?.text,
          order: step?.order,
          sessionStepId: step._id,
          review: 0,
          skippable: true,
          ownId: step._id,
          exerciseDecided: !!step.myExerciceId,
          displayResult: step?.displayResult,
          needTagRefetch: false,
          needImmediateTagRefetch: false,
          type: session?.sessionType
        });
      }).filter(x=>x).sort((a: any, b: any) => {
        return a.order > b.order ? 1 : -1;

      });

      const sessionData =  {
        _id: session._id,
        sessionSteps,
        title: session.title,
        shortDescription: session.shortDescription,
        order: session.order,
        userSessionId: session?.userSession?._id,
        userSessionOrder: session?.userSession?.order,
        wasDoneToday: session?.userSession?.wasDoneToday,
        done: false,
        duration: session?.duration,
        contentLoaded: false,
        summary: session?.summary,
        language: (getParent(self) as any).language.name.key,
        fromPath: false,
        fromReckoning: false,
        path: null,
        reckoning:  null,
        lastFrameIndex: session?.lastFrameIndex,
        userSegments: session?.userSegments,
        type: session?.sessionType,
        dateCreated: session?.dateCreated
      };

      const index = self.session?.findIndex(s => s && s._id === session._id);
      if (index === -1) {
        self.session?.push(sessionData);
        (getParent(self) as any).user.incrementNbAiSession();
      } else {
        self.session[index] = sessionData;
      }
      loadContentOfSession(null, session._id);
    };



    const clear = () => {
      self.session = cast([]);
      self.state = 'done';
    };


    // UserSurvey.questions[].answers are refs to answer DOCUMENTS: the
    // synthetic ids minted by the scheduling/instant-help dynamic questions
    // ("assistanceMode-phone", "2026-08-24-09:00") reference nothing and are
    // dropped — the user's choice already drove the booking
    const OBJECT_ID = /^[a-f\d]{24}$/i;
    const answerRefs = (response: IResponse): string[] =>
      response?.specialTypeOfAnswer
        ? []
        : (response.answers ?? []).map((a: any) => a.id).filter((id: string) => OBJECT_ID.test(id));

    const getAnswersFromSurveyResponses = (surveyResponses?: IResponse[]) => {
      if (!surveyResponses) {
        return [];
      }
      return surveyResponses.map(response => ({
        question: response.questionId,
        answers: answerRefs(response),
        value: response.value || null,
        freeText: response?.specialTypeOfAnswer ? (response.answers?.[0] as any)?.id || null : (response.freeText || null),
        importance: response.importance || null,
        specialTypeOfAnswer: response?.specialTypeOfAnswer || null,
      }));
    };



    const endOfSession: (pathId: string | null, sessionId: string, times?: number[]) => Promise<void> = flow(function* (pathId, sessionId, times) {
      const session = findSession(pathId, sessionId);
      if (session) {
        const globalTimeSpent = times && times.reduce((acc, cur) => {
          return acc + cur / 1000;
        }, 0);

        const rootStore = getRoot<any>(self);
        const treeNav = rootStore.treeNav as ITreeNav;

        const userSessionStepInput = treeNav.path?.map((step, index): SessionStepInput => {
          const questionId = step.dataRef?.question?._id;
          // lastUserSurvey meta is added to FEATURE nodes in endOfSurveyMutation in SessionStore
          const userSurvey = step.getMetaValue('lastUserSurvey') || null;
          let response: IResponse | undefined;
          if (questionId) {
            response = (rootStore.responses as IResponseStore).getResponse({
              questionId,
              sessionId,
              pathId: treeNav.content.path?._id,
            });
          }
          return {
            userSurvey,
            answers: response?.answers?.map(answer => answer.id) || null,
            value: response?.value || null,
            freeText: response?.freeText || null,
            contentId: step.dataRef?.dataId || null,
            contentType: step.dataRef?.type || null,
            myExerciceId: step.dataRef?.sessionStep?.myExerciceId || null,
            sessionStep: step.sessionStepId || null,
            review: 0, // TODO where to get this?
            order: index,
          };
        });

        if (session.fromPath) {
          yield client.mutate({
            mutation: UPDATE_USER_SESSION,
            variables: {
              input: {
                '_id': session.userSessionId,
                'timeSpent': globalTimeSpent,
                'sessionSteps': userSessionStepInput
              }
            }
          });
          if (!session.done) {
            (getParent(self) as any).path.increaseNumberSessions(pathId);
            if(pathId === getRoot<IRootStore>(self)?.user?.activePath) {
              getRoot<IRootStore>(self)?.user?.stat?.incrementNumberOfSessionsDone();
            }

          }
          getRoot<IRootStore>(self).path.fetchPath(pathId);
        }
        session.done = true;
        session.wasDoneToday = true;
        if (session.fromReckoning) {
          yield client.mutate({
            mutation: CREATE_USER_RECKONING,
            fetchPolicy:'no-cache',
            variables: {
              input: {
                'reckoning': session.reckoning,
                'timeSpent': globalTimeSpent,
                'sessionSteps': userSessionStepInput
              }
            }
          });
          getParent<IRootStore>(self).reckoning.getCurrentReckoning(session.language);
          getRoot<IRootStore>(self).user.fetchReckoningResult();
        }

      }
    });

    const findSession: (pathId: string | null, sessionId: string) => ISession | undefined | null = (pathId: string | null, sessionId: string) => {

      if (pathId) {
        const index = self?.session.findIndex(p => p && p.path === pathId && p.fromPath && p._id === sessionId);
        if (index !== -1) {
          return self.session[index];
        } else {
          return null;
        }
      } else {
        const index = self?.session.findIndex(p => p && p._id === sessionId);
        if (index !== -1) {
          return self.session[index];
        } else {
          return null;
        }
      }
    };

    const findSessionIndex: (pathId: string | null, sessionId: string) => number = (pathId: string | null, sessionId: string) => {

      if (pathId) {
        const index = self?.session.findIndex(p => p && p.path === pathId && p.fromPath && p._id === sessionId);
        return index;
      } else {
        const index = self?.session.findIndex(p => p && p._id === sessionId);
        return index;
      }
    };

    const endOfSurveyMutation: (
      pathId: string | null,
      sessionId: string,
      surveyId: string,
      reckoningId?: string | null,
    ) => Promise<void> = flow(function* (pathId, sessionId, surveyId, reckoningId = null) {
      const session = findSession(pathId, sessionId);
      if (session) {
        // Get survey responses from treeNav.choices
        const rootStore = getRoot<any>(self);
        const treeNav = rootStore.treeNav as ITreeNav;
        const surveyResponses = treeNav.choices.finalised;
        console.log('End of survey mutation', { surveyId, surveyResponses: JSON.stringify(surveyResponses, null, 2) });
        if (!surveyId) {
          captureException(new Error(`endOfSurveyMutation without a block id (session ${sessionId})`));
          return;
        }
        // the block's type decides what to do with the score; make sure it is loaded
        yield rootStore.survey.loadSurvey(surveyId);
        const survey = rootStore.survey.getSurvey(surveyId);
        logAmplitude('Activity Finished', {
          activityType: 'self_assessment',
          activityId: survey?.humanReadableId,
          surveyType: survey?.type,
        });
        const input = {
          user: rootStore.user.user._id,
          survey: surveyId,
          questions: getAnswersFromSurveyResponses(surveyResponses),
          // the reckoning being answered (route param), else the one this session copy was loaded for
          reckoning: reckoningId || session.reckoning || null,
        };
        if (survey?.type === 'matching') {
          // a matching block ranks specialists instead of scoring features
          rootStore.user.matchingSurveyDone();
          yield clientFunction(session.language).mutate({
            mutation: FINISH_MATCHING_SURVEY,
            fetchPolicy: 'no-cache',
            variables: { input: { user: input.user, survey: input.survey, questions: input.questions } },
          });
          return;
        }

        const features = yield clientFunction(session.language).mutate({
          mutation: FINISH_SURVEY,
          fetchPolicy:'no-cache',
          variables: { input }
        });
        if (survey?.type === 'MHC') {
          rootStore.user.MHCDone();
          setProperty({ MHCShown: false });
        }
        if (!rootStore.user.anonymous) {
          rootStore.user.fetchProfile(rootStore.language.language);
        }
        // assign lastUserSurvey to tree nodes with dataRef.type === 'FEATURE'
        // this is used later in UPDATE_USER_SESSION (end of sesssion)
        if (features?.data?.createUserSurveyFeature?.[0]?.lastUserSurvey) {
          const lastUserSurvey = features.data.createUserSurveyFeature[0].lastUserSurvey;
          const featureNodes = treeNav.tree?.findNodesByDataRefType('FEATURE');
          featureNodes?.forEach(node => {
            node.addOrUpdateMeta('lastUserSurvey', lastUserSurvey);
          });
        }
        features && features.data && features.data.createUserSurveyFeature && Array.isArray(features.data.createUserSurveyFeature) &&
                features.data.createUserSurveyFeature.forEach((feat: any) => {
                  (getParent(self) as any).feature.loadFeatureFromUserFeature({
                    ...filterDefinedFields(feat.feature,FeatureModel.properties),
                    _id: feat.feature?._id,
                    value: feat.value,
                    _id: feat.feature?._id,
                    importance: feat.importance,
                    color: feat.interval?.color,
                    name: feat.interval?.label?.name,
                    description: feat.interval?.label?.description,
                    descriptionForSurvey: feat.interval?.label?.descriptionForSurvey,
                    logo: feat.interval?.logo,
                  }, session.language);
                });
      }

      if(!pathId && !(getParent(self) as any).user.anonymous) {
        (getParent(self) as any).user.fetchRecommendedPath();
      }
    });

    /** the id of the session playing a special flow (mhc, matching, instantHelp, scheduling) */
    const fetchSessionIdOfType = flow(function* (type: string) {
      try {
        const { data } = yield clientFunction((getParent(self) as any).language.name.key).query({
          query: SESSION_OF_TYPE,
          variables: { sessionType: type },
          fetchPolicy: 'network-only',
        });
        return data?.sessionOfType?._id || null;
      } catch (error) {
        captureException(error);
        return null;
      }
    });

    const loadContentFromSession = flow(function* (session: ISession) {
      if (session.sessionSteps) {
        // the question sets of this session's standalone questions, loaded
        // once each with their questions and dynamic answers
        const setIds = [...new Set(session.sessionSteps
          .filter(ss => ss.contentType === 'QUESTION' && ss.questionSet)
          .map(ss => ss.questionSet as string))];
        yield Promise.all(setIds.map(id => (getParent(self) as any).survey.loadSurvey(id, true, true)));
        return yield Promise.all(session.sessionSteps.map(async ss => {
          switch (ss.contentType) {
          case 'CONTENT_TEXT':
          case 'CONTENT_VIDEO':
          case 'CONTENT_AUDIO':
            (getParent(self) as any).content.fetchContentIfNotExist(ss.contentId);
            break;
          case 'SURVEY':
            await (getParent(self) as any).survey.fetchSurveyIfNotExist(ss.contentId, true);
            break;
          case 'QUESTION':
            // a question of a set came with its set (above)
            (getParent(self) as any).question.fetchQuestionIfNotExist(ss.contentId);
            break;
          case 'EXERCISE':
            (getParent(self) as any).exercise.fetchExerciseIfNotExist(ss.contentId);
            break;
          case 'ACTIVITY': {
            // the activity frames are grafted into this session's tree:
            // its own session (steps + content) must be loaded too — and
            // refreshed, so the grafted nodes always resolve to current steps
            await (getParent(self) as any).activity.fetchActivityIfNotExist(ss.contentId);
            const activity = (getParent(self) as any).activity.getActivity(ss.contentId);
            console.log('Session.loadContentFromSession activity', ss.contentId, '-> session', activity?.sessionId);
            if (activity?.sessionId) {
              // through the store action, not the bare flow: after an await
              // there is no MST action context and the bare flow throws
              // ("a mst flow must always have a parent context"), rejecting
              // the whole content load silently
              await (self as any).fetchSessionWrapperIfNotExistIndependent(activity.sessionId, true, true);
            }
            break;
          }
          default: break;
          }
        }));
      }
    });


    const loadContentOfSession = flow(function* (pathId: string | null, sessionId: string) {
      const session = findSession(pathId, sessionId);
      if (session && session.sessionSteps) {
        yield loadContentFromSession(session);
        // a refetch meanwhile (session entry, language change) replaces the
        // node: flag the one now in the store, not the detached one
        const current = findSession(pathId, sessionId);
        if (current) {
          current.contentLoaded = true;
        }
        console.log('Session.loadContentOfSession done', sessionId, 'contentLoaded', !!current);
      } else {
        console.log('Session.loadContentOfSession: session not found', pathId, sessionId);
      }
    });

    const addFramesWrapper = (pathId: string | null, sessionId: string) => {
      const session = findSession(pathId, sessionId);
      if(session && !session.contentLoaded) {
        session.contentLoaded = true;
      }
    };


    const refetchUserSegments= flow(function* (sessionId: string)  {
      const session = findSession(null, sessionId);
      if(!session) return null;
      const languageRequest = (getParent(self) as any).language.name.key;
      try {
        const { data }: any = yield clientFunction(languageRequest).query({ query: GET_SESSION_USER_SEGMENTS, variables: { 'id': sessionId }, fetchPolicy: 'no-cache' });
        if (data &&  'session' in data && 'userSegments' in data.session) {
          session.userSegments = data?.session?.userSegments;
        }
      } catch (error) {
        captureException(error);
        return null;
      }
    });

    const refetchUserSegmentsAsync= async (sessionId: string)  => {
      await refetchUserSegments(sessionId);
    };


    const deleteSessionData = (sessionId: string) => {
      const sessionIndex = findSessionIndex(null,sessionId);
      self.session.splice(sessionIndex,1);
    };

    return {
      fetchSession,
      loadSession,
      loadAISessions,
      loadAISession,
      clear,
      endOfSurveyMutation,
      fetchSessionIdOfType,
      addFramesWrapper,
      findSession,
      findSessionIndex,
      endOfSession,
      fetchSessionWrapperIfNotExistFromPath,
      fetchSessionWrapperIfNotExistFromReckoning,
      fetchSessionWrapperIfNotExistIndependent,
      loadContentFromSession,
      loadContentOfSession,
      refetchUserSegments,
      refetchUserSegmentsAsync,
      deleteSessionData,
    };
  })

  .views(self => ({
    getSessionInfo(pathId: string | null, sessionId: string) {
      if (pathId) {
        const index = self?.session.findIndex(p => p && p.path === pathId && p.fromPath && p._id === sessionId);
        if (index !== -1) {
          return self.session[index];
        } else {
          return null;
        }
      } else {
        const index = self?.session.findIndex(p => p && p._id === sessionId);
        if (index !== -1) {
          return self.session[index];
        } else {
          return null;
        }
      }
    },
    getPathSession(pathId: string | null) {
      if(!self?.session || self?.session.length==0) {
        return [];
      } else {
        return self?.session && self.session
          .filter(s => s.fromPath && s?.path === pathId)
          .sort((a: any, b: any) => {
            return a.order > b.order ? 1 : -1;
          });
      }
    },
    getPathSessionSorted(pathId: string | null) {
      const path = (getParent(self) as any).path.getPath(pathId);
      if( !self?.session || !pathId ||self?.session.length==0) {
        return [];
      }  else {
        if (path?.status === 'started') {
          return self?.session
            .filter((session: ISession) => session.fromPath && session?.path === pathId && session.userSessionId)
            .sort((a: ISession, b: ISession) => {
              if (a.userSessionOrder && b.userSessionOrder) {
                return a.userSessionOrder > b.userSessionOrder ? 1 : -1;
              } else {
                return a.order > b.order ? 1 : -1;
              }
            });
        } else {
          return self?.session
            .filter((session: ISession) => session.fromPath && session?.path === pathId)
            .sort((a: ISession, b: ISession) => (a.order > b.order ? 1 : -1));
        }
      }
    },
    getSessionSummary(pathId: string | null) {
      const sessions = this.getPathSessionSorted(pathId);
      return sessions.map((session: any) => {
        if (session.summary) {
          const values = session.summary.match(/<li>(.*?)<\/li>/g);
          if (values && values.length > 0) {
            return values?.map((val: string) => val.replace(/<\/?li>/g, ' '));
          } else {
            return null;
          }
        } else {
          return null;
        }
      });
    },
    getSessionsAverageDuration(pathId: string | null) {
      const sessions = this.getPathSessionSorted(pathId);
      if(!sessions || sessions.length === 0) return 0;
      const duration = sessions.reduce((acc, cur) => {
        if (!!cur && cur?.duration) {
          return acc + cur.duration;
        }
        return acc;
      }, 0);
      return duration? Math.round(duration / sessions.length):0;
    },
    get aiSessions() {
      return self.session.filter(s => s?.type === 'ai');
    },
    getCurrentSession(pathId: string) {
      if(!pathId) return null;
      const path = getRoot<IRootStore>(self).path.getPath(pathId);
      const todaySessions = this.getTodaySessions(pathId);
      if(!todaySessions) return null;
      return todaySessions.find(ts => ts._id === path?.latestTodaySession);

    },
    getTodaySessions(pathId: string) {
      if(!pathId) return null;
      const path = getRoot<IRootStore>(self).path.getPath(pathId);
      return path?.todaySessionsOrdered;

    },
  }));

export type ISessionStore = Instance<typeof SessionStore>
